/**
 * Cross-page channel to open the LexiaVoiceWidget in a specific mode.
 *
 * The provider mounts inside AppShell; the widget subscribes with
 * `useLexiaVoiceRequests()` to know when to auto-open in a given mode
 * (currently: 'general' or 'daily_voice'). Any authenticated page can call
 * `openInMode()` to trigger the widget without doing DOM tricks. The optional
 * second argument is a hint payload (e.g. target words already known to the
 * caller) so the widget can render the correct banner immediately, before
 * the token round-trip resolves.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const LexiaVoiceContext = createContext(null);

export function LexiaVoiceProvider({ children }) {
    const [request, setRequest] = useState(null); // {mode, at, hint}
    const requestSeq = useRef(0);

    const openInMode = useCallback((mode, hint) => {
        requestSeq.current += 1;
        setRequest({
            mode: mode || "general",
            hint: hint || null,
            at: Date.now(),
            seq: requestSeq.current,
        });
    }, []);

    const clearRequest = useCallback(() => setRequest(null), []);

    return (
        <LexiaVoiceContext.Provider value={{ request, openInMode, clearRequest }}>
            {children}
        </LexiaVoiceContext.Provider>
    );
}

export function useLexiaVoice() {
    const ctx = useContext(LexiaVoiceContext);
    if (!ctx) return { request: null, openInMode: () => {}, clearRequest: () => {} };
    return ctx;
}

/**
 * Convenience for the widget: consumes one request at a time and calls the
 * callback with the requested mode + optional hint. Subsequent identical
 * modes still fire (bumped `seq`) so a user clicking the card twice re-opens.
 */
export function useLexiaVoiceRequests(handler) {
    const { request, clearRequest } = useLexiaVoice();
    useEffect(() => {
        if (!request) return;
        handler(request.mode, request.hint);
        clearRequest();
    }, [request, handler, clearRequest]);
}
